// ============================================================================
// AccueilView — tableau de bord social GS Gestion (E1a lecture + E1b ecritures)
// ============================================================================
// Landing de l'app — portage PoC gs-pulse rendu MAISON @gs/ui. E1b rend le fil
// INTERACTIF : composer actif (PublicationComposer), réactions toggle,
// commentaires dépliables (PublicationComments) — mutations OPTIMISTES avec
// rollback (useAccueilMutations), erreurs en bandeau role=alert mappé
// gest:accueil.errors.* (GA-21, jamais de code brut). Les affordances viennent
// du SERVEUR : canPublish (composer), myReaction (état actif), canDelete par
// commentaire — bind-to-fetch, le client ne calcule aucune règle d'accès.
// Contrat visuel = les 2 mocks figés David (desktop + mobile) :
//   - ≥md : grille 2 colonnes (fil 1.85fr + rail 1fr) ;
//   - <md : 1 colonne, INDICATEURS EN TÊTE (chiffre clé d'abord), puis
//     Actualité, puis Comités/Santé sous le fil.
// Cartes management (Indicateurs / Comités / Santé) : gatées AVANT fetch sur
// viewer.canViewManagement du fil (FIX-403 D2/D3 — zéro requête refusée émise,
// contrôles capability-aware §4c) — un employee ne voit que le fil public.
// États obligatoires : Skeleton (isLoading), EmptyState explicite (cas NOMINAL
// — tables gest.* vides), erreur avec onRetry ; 503 = bandeau migration.

import { useState, useMemo } from 'react';
import {
  PageHeader, GsSegmented, PublicationCard, SkeletonCard, QueryError, EmptyState,
  GS_COMPONENTS,
} from '@gs/ui';
import type { GsSegmentedOption } from '@gs/ui';
import type { TFunction } from 'i18next';
import { Megaphone } from 'lucide-react';
import { useAuth } from '@gs/auth';
import { useTranslation } from '../../i18n';
import {
  usePublicationsFeed, useIndicatorsSummary, useCommitteesActivity, useTeamsHealth,
  isMigrationPending, accueilErrorCode, PUBLICATION_CATEGORIES,
} from '../../hooks/useAccueil';
import type { Publication, PublicationCategory, ReactionType } from '../../hooks/useAccueil';
import { useToggleReaction, type OptimisticAuthor } from '../../hooks/useAccueilMutations';
import { useBlobObjectUrl } from '../../lib/blobs/objectUrl';
import { formatDayDate } from './format';
import { feedCardProps } from './feedCardProps';
import { IndicatorsCard } from './IndicatorsCard';
import { CommitteesActivityCard } from './CommitteesActivityCard';
import { TeamsHealthCard } from './TeamsHealthCard';
import { QuickLinksCard } from './QuickLinksCard';
import { PublicationComposer } from './PublicationComposer';
import { PublicationEditor } from './PublicationEditor';
import { PublicationComments } from './PublicationComments';
import { FeedAttachments } from './FeedAttachments';
import { AccueilTeamView } from './AccueilTeamView';
import { useMonEquipeAccess } from '../../hooks/useMonEquipe';

/** Codes courts d'action mappés gest:accueil.errors.* (GA-21). */
const KNOWN_ACTION_ERRORS = [
  'forbidden', 'invalid', 'notfound', 'server', 'too_large', 'type_refused',
  'attachments_partial', 'download_failed',
];

/** Classes du rail : la carte Indicateurs n'y apparait qu'a partir de md. */
const DESKTOP_ONLY = 'hidden md:block';

type HomeView = 'overview' | 'team';
type CategoryFilter = 'all' | PublicationCategory;

/**
 * Une carte du fil. Composant a PART ENTIERE (module-level, jamais defini dans
 * le rendu — sinon React remonterait l'arbre a chaque frappe) pour deux raisons :
 *  1. l'image passe par un HOOK (useBlobObjectUrl) — illegal dans un .map() ;
 *  2. chaque carte resout SON image independamment (une image lente ne retient
 *     pas le fil, une image manquante n'affecte que sa carte).
 * L'image est servie par PROXY sous Bearer (CHORE-GEST-BLOBS) : tant qu'elle
 * n'est pas resolue — ou si le blob/storage manque — `imageObjectUrl` est undefined et
 * la carte se rend simplement SANS image (fallback conserve, aucun cadre casse).
 */
function FeedCard({
  p, locale, t, author, expanded, onToggleComments, onReact, onError,
}: {
  p: Publication;
  locale: string;
  t: TFunction;
  author: OptimisticAuthor | null;
  expanded: boolean;
  onToggleComments: (id: string) => void;
  onReact: (key: ReactionType) => void;
  onError: (code: string) => void;
}) {
  // Entrees optimistes (id temporaire) : pas d'interactions tant que le serveur
  // n'a pas confirme (reconciliation onSettled) — et aucune image a resoudre.
  const optimistic = p.id.startsWith('optimistic-');
  const imageObjectUrl = useBlobObjectUrl(
    p.hasImage && !optimistic ? `/api/publications/${p.id}/image` : null,
  );

  return (
    <PublicationCard
      {...feedCardProps(p, locale, t)}
      imageObjectUrl={imageObjectUrl}
      onReact={optimistic ? undefined : (key) => onReact(key as ReactionType)}
      onToggleComments={optimistic ? undefined : () => onToggleComments(p.id)}
      commentsExpanded={expanded}
    >
      {!optimistic && p.attachments && p.attachments.length > 0 && (
        <FeedAttachments
          publicationId={p.id}
          attachments={p.attachments}
          locale={locale}
          t={t}
          onError={onError}
        />
      )}
      {expanded && author && !optimistic && (
        <PublicationComments
          publicationId={p.id}
          author={author}
          locale={locale}
          t={t}
          onError={onError}
        />
      )}
    </PublicationCard>
  );
}

export default function AccueilView() {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const [view, setView] = useState<HomeView>('overview');
  const [category, setCategory] = useState<CategoryFilter>('all');
  // Interactions E1b : zones commentaires dépliées + bandeau d'erreur d'action.
  const [openComments, setOpenComments] = useState<ReadonlySet<string>>(new Set());
  const [actionError, setActionError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const toggleReaction = useToggleReaction();

  // Gating Mon equipe (M-150) : probe GET /api/mon-equipe — 403 manager/employee => onglet masque.
  const monEquipeAccess = useMonEquipeAccess(!!user);
  const canViewMonEquipe = monEquipeAccess.data?.canView === true;
  // Vue effective au rendu : seul `false` replie (undefined pendant chargement =
  // pas de flash overview). Doctrine anti-faux du repo.
  const effectiveView: HomeView =
    view === 'team' && monEquipeAccess.data?.canView === false ? 'overview' : view;

  const feed = usePublicationsFeed(category === 'all' ? null : category, effectiveView === 'overview');

  // Gating capability-aware AVANT fetch (FIX-403 D2/D3, anti « delegated 403
  // stream ») : le verdict serveur du fil (viewer.canViewManagement) décide si
  // les 3 requêtes management PARTENT (enabled) ET si leurs cartes se MONTENT —
  // un employé n'émet AUCUNE requête refusée. La garde serveur 403 des routes
  // indicators/committees/teams reste l'autorité (D4).
  const viewer = feed.data?.pages[0]?.viewer;
  const canViewManagement = viewer?.canViewManagement ?? false;

  const indicators = useIndicatorsSummary(canViewManagement);
  const committees = useCommitteesActivity(canViewManagement);
  const teams = useTeamsHealth(canViewManagement);

  const firstName = (user?.name ?? '').trim().split(/\s+/)[0] ?? '';

  const viewOptions: GsSegmentedOption[] = useMemo(() => {
    const opts: GsSegmentedOption[] = [
      { value: 'overview', label: t('gest:accueil.view.overview') },
    ];
    if (canViewMonEquipe) {
      opts.push({ value: 'team', label: t('gest:accueil.view.team') });
    }
    return opts;
  }, [canViewMonEquipe, t]);

  const categoryOptions: GsSegmentedOption[] = [
    { value: 'all', label: t('gest:accueil.categories.all') },
    ...PUBLICATION_CATEGORIES.map((c) => ({ value: c, label: t(`gest:accueil.categories.${c}`) })),
  ];

  const publications: Publication[] = feed.data?.pages.flatMap((p) => p.publications) ?? [];
  const canPublish = feed.data?.pages[0]?.viewer.canPublish ?? false;
  const author = user ? { email: user.email ?? '', name: user.name ?? null } : null;

  function reportActionError(code: string) {
    setActionError(KNOWN_ACTION_ERRORS.includes(code) ? code : 'server');
  }

  function toggleCommentsFor(id: string) {
    setOpenComments((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // L'editeur de publication s'ouvre A LA PLACE de l'accueil (pas de route
  // neuve) ; il garde l'affordance canPublish du serveur. L'etape visuel suit
  // canAttachImage, verdict serveur (publier ET stockage configure). Les pieces
  // jointes multiples suivent canAttachFiles, verdict serveur (les memes ET la
  // table des pieces jointes presente, mig 285).
  if (editing && canPublish && author) {
    return (
      <PublicationEditor
        author={author}
        locale={locale}
        t={t}
        canAttachImage={feed.data?.pages[0]?.viewer.canAttachImage ?? false}
        canAttachFiles={feed.data?.pages[0]?.viewer.canAttachFiles ?? false}
        nextInFeed={publications[0]}
        onClose={() => setEditing(false)}
        onError={reportActionError}
      />
    );
  }

  const indicatorsCard = (className?: string) => (
    <IndicatorsCard
      data={indicators.data}
      isLoading={indicators.isLoading}
      isError={indicators.isError}
      migrationPending={isMigrationPending(indicators.error)}
      onRetry={() => void indicators.refetch()}
      locale={locale}
      t={t}
      className={className}
    />
  );

  return (
    <div className="space-y-5">
      <PageHeader
        title={t('gest:accueil.greeting', { name: firstName })}
        description={formatDayDate(new Date(), locale)}
        onRefresh={() => void feed.refetch()}
        refreshLoading={feed.isFetching}
        actions={
          <div className="w-full sm:w-auto">
            <GsSegmented
              ariaLabel={t('gest:accueil.view.toggleAria')}
              options={viewOptions}
              value={effectiveView}
              onChange={(v) => setView(v as HomeView)}
              fullWidth
            />
          </div>
        }
      />

      {effectiveView === 'team' ? (
        <AccueilTeamView enabled={canViewMonEquipe} />
      ) : (
        <div className="grid grid-cols-1 items-start gap-5 md:grid-cols-[minmax(0,1.85fr)_minmax(0,1fr)]">
          {/* Mobile d'abord : Indicateurs EN TÊTE (chiffre clé avant le fil — mock mobile). */}
          {canViewManagement && <div className="md:hidden">{indicatorsCard()}</div>}

          {/* ── Colonne fil : Actualité ─────────────────────────────────────── */}
          <section>
            <h2 className="text-head font-semibold text-slate-800 dark:text-slate-100">
              {t('gest:accueil.feed.title')}
            </h2>
            <p className="mb-4 mt-0.5 text-field text-slate-500 dark:text-slate-400">
              {t('gest:accueil.feed.subtitle')}
            </p>

            {/* Bandeau d'erreur d'action (publier/réagir/commenter) — GA-21. */}
            {actionError && (
              <div
                role="alert"
                className="mb-4 flex items-start justify-between gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-field text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300"
              >
                <span>{t(`gest:accueil.errors.${actionError}`)}</span>
                <button
                  type="button"
                  onClick={() => setActionError(null)}
                  className={`${GS_COMPONENTS.btnGhost} font-semibold hover:text-red-900 dark:hover:text-red-200`}
                >
                  {t('gest:accueil.errors.dismiss')}
                </button>
              </div>
            )}

            {/* Composer — gestion seulement (affordance canPublish du serveur ;
                la garde POST reste l'autorité). Écritures actives (E1b). */}
            {canPublish && author && (
              <PublicationComposer
                author={author}
                firstName={firstName}
                t={t}
                onOpen={() => setEditing(true)}
              />
            )}

            {/* Barre de filtres catégories — GsSegmented scrollable (seul débordement
                horizontal volontaire de l'écran, décision figée MASTER §3). */}
            <GsSegmented
              variant="scrollable"
              ariaLabel={t('gest:accueil.feed.filterAria')}
              options={categoryOptions}
              value={category}
              onChange={(v) => setCategory(v as CategoryFilter)}
              className="mb-4 pb-1"
            />

            {feed.isLoading ? (
              <div className="space-y-3.5">
                <SkeletonCard lines={4} />
                <SkeletonCard lines={4} />
              </div>
            ) : feed.isError ? (
              isMigrationPending(feed.error) ? (
                <p className="text-field text-slate-400 dark:text-slate-500">
                  {t('gest:accueil.migrationPending')}
                </p>
              ) : (
                <QueryError message={t('gest:accueil.feed.error')} onRetry={() => void feed.refetch()} />
              )
            ) : publications.length === 0 ? (
              // Cas NOMINAL actuel : tables vides tant que la migration data (frag dédié) n'a pas tourné.
              <EmptyState
                title={t('gest:accueil.feed.emptyTitle')}
                description={t('gest:accueil.feed.empty')}
                icon={<Megaphone size={40} className="text-slate-300 dark:text-slate-600" aria-hidden />}
              />
            ) : (
              <div className="space-y-3.5">
                {publications.map((p) => (
                  <FeedCard
                    key={p.id}
                    p={p}
                    locale={locale}
                    t={t}
                    author={author}
                    expanded={openComments.has(p.id)}
                    onToggleComments={toggleCommentsFor}
                    onReact={(key) =>
                      toggleReaction.mutate(
                        { publicationId: p.id, type: key, current: p.myReaction },
                        { onError: (err) => reportActionError(accueilErrorCode(err)) },
                      )
                    }
                    onError={reportActionError}
                  />
                ))}
                {feed.hasNextPage && (
                  <button
                    type="button"
                    onClick={() => void feed.fetchNextPage()}
                    disabled={feed.isFetchingNextPage}
                    className={`${GS_COMPONENTS.btnSecondary} w-full`}
                  >
                    {feed.isFetchingNextPage ? t('gest:accueil.feed.loading') : t('gest:accueil.feed.loadMore')}
                  </button>
                )}
              </div>
            )}
          </section>

          {/* ── Rail (desktop a droite ; mobile : sous le fil) — acces rapide perso
              pour tous ; cartes management seulement si canViewManagement (D3). */}
          <section>
            <h2 className="text-head font-semibold text-slate-800 dark:text-slate-100">
              {t('gest:accueil.rail.title')}
            </h2>
            <p className="mb-4 mt-0.5 text-field text-slate-500 dark:text-slate-400">
              {t('gest:accueil.rail.subtitle')}
            </p>
            <div className="space-y-3.5">
              <QuickLinksCard canViewMonEquipe={canViewMonEquipe} />
              {canViewManagement ? (
                <>
                  {indicatorsCard(DESKTOP_ONLY)}
                  <CommitteesActivityCard
                    data={committees.data}
                    isLoading={committees.isLoading}
                    isError={committees.isError}
                    migrationPending={isMigrationPending(committees.error)}
                    onRetry={() => void committees.refetch()}
                    locale={locale}
                    t={t}
                  />
                  <TeamsHealthCard
                    data={teams.data}
                    isLoading={teams.isLoading}
                    isError={teams.isError}
                    migrationPending={isMigrationPending(teams.error)}
                    onRetry={() => void teams.refetch()}
                    locale={locale}
                    t={t}
                  />
                </>
              ) : null}
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
